/**
 * Extra cart behaviour for the drawer and the /cart page: discount codes,
 * upsell / bestseller quick add, free shipping bar in a foreign currency.
 *
 * All of these are custom elements so they keep working after
 * <cart-drawer-items> swaps ".drawer__inner" with fresh section HTML: the
 * browser auto-upgrades any new custom element it finds in the inserted
 * markup, there is nothing to re-bind manually.
 */

(function () {
	function getCartDrawer() {
		return document.querySelector("cart-drawer");
	}

	// renderContents swaps the whole drawer markup, so the focused element is
	// gone afterwards. Move focus back into the fresh markup.
	function refocusInDrawer(selector) {
		setTimeout(() => {
			const target =
				document.querySelector(selector) ||
				document.querySelector("#CartDrawer .drawer__close");
			if (target) target.focus();
		}, 50);
	}

	function cartDrawerSectionIds(cartDrawer) {
		return cartDrawer
			? cartDrawer.getSectionsToRender().map((section) => section.id)
			: [];
	}

	// renderContents() also calls open(), which queues a one-time transitionend
	// handler that moves focus to .drawer__inner. On an already open drawer the
	// next transition inside it fires that handler and steals focus, so only swap
	// the sections there.
	function renderDrawer(cartDrawer, response) {
		if (!cartDrawer.classList.contains("active")) {
			cartDrawer.renderContents(response);
			return;
		}
		const inner = cartDrawer.querySelector(".drawer__inner");
		if (inner) inner.classList.remove("is-empty");
		cartDrawer.getSectionsToRender().forEach((section) => {
			const target = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			const html = response.sections && response.sections[section.id];
			if (!target || !html) return;
			target.innerHTML = cartDrawer.getSectionInnerHTML(html, section.selector);
		});
		// The swap replaced the overlay and the note buttons, rebind what open() binds
		const overlay = cartDrawer.querySelector("#CartDrawer-Overlay");
		if (overlay) overlay.addEventListener("click", cartDrawer.close.bind(cartDrawer));
		cartDrawer.addCloseButtonHandlers();
	}

	// On the /cart page the extras live inside <cart-items>. They re-render the
	// whole main-cart section instead of the drawer, since renderContents()
	// would open the drawer on top of the page.
	function getMainCart(element) {
		return element.closest("cart-items");
	}

	function mainCartSectionId() {
		const items = document.getElementById("main-cart-items");
		return items ? items.dataset.id : null;
	}

	function sectionIdsFor(element) {
		if (getMainCart(element)) {
			return [mainCartSectionId(), "cart-icon-bubble"].filter(Boolean);
		}
		return cartDrawerSectionIds(getCartDrawer());
	}

	function renderMainCart(mainCart, response) {
		const sections = response.sections || {};
		const html = sections[mainCartSectionId()];
		if (html) {
			const source = new DOMParser()
				.parseFromString(html, "text/html")
				.querySelector("cart-items");
			if (source) {
				mainCart.innerHTML = source.innerHTML;
				mainCart.classList.toggle("is-empty", source.classList.contains("is-empty"));
			}
		}
		const bubble = document.getElementById("cart-icon-bubble");
		if (bubble && sections["cart-icon-bubble"]) {
			const source = new DOMParser()
				.parseFromString(sections["cart-icon-bubble"], "text/html")
				.querySelector(".shopify-section");
			if (source) bubble.innerHTML = source.innerHTML;
		}
	}

	function refocus(selector) {
		setTimeout(() => {
			const target = document.querySelector(selector);
			if (target) target.focus();
		}, 50);
	}

	class CartDiscountForm extends HTMLElement {
		constructor() {
			super();
			this.input = this.querySelector("input");
			this.applyButton = this.querySelector(".cart-discount__apply");
			this.errorElement = this.querySelector(".cart-discount__form-error");

			if (this.applyButton) {
				this.applyButton.addEventListener("click", this.onApply.bind(this));
			}
			if (this.input) {
				this.input.addEventListener("keydown", (event) => {
					if (event.key === "Enter") {
						event.preventDefault();
						this.onApply();
					}
				});
			}
			const toggle = this.querySelector(".cart-discount__toggle");
			if (toggle && this.input) {
				toggle.addEventListener("toggle", () => {
					if (toggle.open) this.input.focus();
				});
			}
			// On the /cart page this form sits inside <cart-items>, whose change
			// listener would treat the code input as a quantity field
			this.addEventListener("change", (event) => event.stopPropagation());
			this.addEventListener("click", (event) => {
				const removeButton = event.target.closest(".cart-discount__remove");
				if (!removeButton) return;
				event.preventDefault();
				const chip = removeButton.closest("[data-code]");
				if (chip) this.onRemove(chip.dataset.code);
			});
		}

		getExistingCodes() {
			return Array.from(this.querySelectorAll("[data-code]")).map(
				(element) => element.dataset.code
			);
		}

		onApply() {
			if (!this.input) return;
			const code = this.input.value.trim();
			if (!code) return;
			this.pendingCode = code;

			const codes = this.getExistingCodes().filter(
				(existing) => existing.toLowerCase() !== code.toLowerCase()
			);
			codes.push(code);
			this.updateDiscounts(codes);
		}

		onRemove(code) {
			this.pendingCode = null;
			const codes = this.getExistingCodes().filter((existing) => existing !== code);
			this.updateDiscounts(codes);
		}

		setLoading(loading) {
			if (!this.applyButton) return;
			this.applyButton.classList.toggle("loading", loading);
			this.applyButton.disabled = loading;
		}

		updateDiscounts(codes) {
			if (this.errorElement) this.errorElement.textContent = "";
			this.setLoading(true);

			const cartDrawer = getCartDrawer();
			const mainCart = getMainCart(this);
			const inputSelector = `#${this.id}-Input`;
			const body = JSON.stringify({
				discount: codes.join(","),
				sections: sectionIdsFor(this),
				sections_url: window.location.pathname,
			});

			fetch(`${window.routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
				.then((response) => response.json())
				.then((response) => {
					if (mainCart) {
						renderMainCart(mainCart, response);
						refocus(inputSelector);
					} else {
						if (cartDrawer) renderDrawer(cartDrawer, response);
						refocusInDrawer(inputSelector);
					}
					// "cart-items" keeps <cart-drawer-items> from swapping in the main-cart section
					publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
					const pendingCode = this.pendingCode;
					this.pendingCode = null;
					if (!pendingCode) return;
					// Shopify drops unknown codes from the cart silently, so check the
					// response and tell the customer when the code did not stick.
					const applied = (response.discount_codes || []).some(
						(discount) =>
							discount.applicable &&
							discount.code.toLowerCase() === pendingCode.toLowerCase()
					);
					if (applied) return;
					const form = document.getElementById(this.id) || this;
					// The re-rendered field starts collapsed, open it so the error shows
					const toggle = form.querySelector(".cart-discount__toggle");
					if (toggle) toggle.open = true;
					const errorElement = form.querySelector(".cart-discount__form-error");
					if (errorElement && !form.querySelector(".cart-discount__error")) {
						errorElement.textContent = form.dataset.invalidMessage;
					}
				})
				.catch((error) => {
					console.error(error);
					if (this.errorElement) {
						this.errorElement.textContent = window.cartStrings.error;
					}
				})
				.finally(() => {
					this.setLoading(false);
				});
		}
	}

	if (!customElements.get("cart-discount-form")) {
		customElements.define("cart-discount-form", CartDiscountForm);
	}

	class CartQuickAdd extends HTMLElement {
		constructor() {
			super();
			this.button = this.querySelector("button");
			if (this.button) this.button.addEventListener("click", this.onClick.bind(this));
		}

		// "_" properties stay hidden from customers but end up on the order line,
		// so a Shopify Flow can tag orders that used the upsell
		lineItem(variantId) {
			const item = { id: variantId, quantity: 1 };
			if (this.dataset.source) item.properties = { _cart_source: this.dataset.source };
			return item;
		}

		onClick(event) {
			event.preventDefault();
			if (!this.button || this.button.disabled) return;

			const variantId = this.dataset.variantId;
			if (!variantId) return;

			const cartDrawer = getCartDrawer();
			const mainCart = getMainCart(this);
			this.button.disabled = true;
			this.button.classList.add("loading");

			const body = JSON.stringify({
				items: [this.lineItem(variantId)],
				sections: sectionIdsFor(this),
				sections_url: window.location.pathname,
			});

			fetch(`${window.routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
				.then((response) => response.json())
				.then((response) => {
					if (response.status) {
						const message = response.description || response.message;
						console.error(message);
						const errors = document.getElementById(
							mainCart ? "cart-errors" : "CartDrawer-CartErrors"
						);
						if (errors) errors.textContent = message;
						return;
					}
					if (mainCart) {
						renderMainCart(mainCart, response);
						refocus("#main-cart-footer .cart__checkout-button");
					} else if (cartDrawer) {
						// product-form.js clears this by hand too, renderContents only
						// clears it on .drawer__inner
						cartDrawer.classList.remove("is-empty");
						renderDrawer(cartDrawer, response);
						refocusInDrawer("#CartDrawer-Checkout");
					}
					publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
				})
				.catch((error) => console.error(error))
				.finally(() => {
					this.button.disabled = false;
					this.button.classList.remove("loading");
				});
		}
	}

	if (!customElements.get("cart-quick-add")) {
		customElements.define("cart-quick-add", CartQuickAdd);
	}

	let cartBestsellersHTML = null;
	let cartBestsellersFetchPromise = null;

	class CartBestsellers extends HTMLElement {
		connectedCallback() {
			if (this.dataset.loaded === "true") return;

			// The /cart page renders the empty state hidden; fetch only once it shows
			if (this.dataset.lazy === "true" && "IntersectionObserver" in window) {
				if (this.observer) return;
				this.observer = new IntersectionObserver((entries) => {
					if (!entries.some((entry) => entry.isIntersecting)) return;
					this.observer.disconnect();
					this.load();
				});
				this.observer.observe(this);
				return;
			}
			this.load();
		}

		disconnectedCallback() {
			if (this.observer) this.observer.disconnect();
			this.observer = null;
		}

		load() {
			if (cartBestsellersHTML) {
				this.renderHTML(cartBestsellersHTML);
				return;
			}

			const url = this.dataset.url;
			if (!url) return;

			if (!cartBestsellersFetchPromise) {
				cartBestsellersFetchPromise = fetch(url).then((response) => response.text());
			}

			cartBestsellersFetchPromise
				.then((html) => {
					cartBestsellersHTML = html;
					this.renderHTML(html);
				})
				.catch((error) => console.error(error));
		}

		renderHTML(html) {
			const doc = new DOMParser().parseFromString(html, "text/html");
			const source = doc.getElementById("CartBestsellers");
			if (!source) return;
			this.innerHTML = source.innerHTML;
			this.dataset.loaded = "true";
			// The section is shared, so the placement sets the line item source
			if (this.dataset.source) {
				this.querySelectorAll("cart-quick-add").forEach((quickAdd) => {
					quickAdd.dataset.source = this.dataset.source;
				});
			}
		}
	}

	if (!customElements.get("cart-bestsellers")) {
		customElements.define("cart-bestsellers", CartBestsellers);
	}

	// Upsell order: Shopify's recommendations for the cart products move to the
	// front, complementary (set in the Search & Discovery app) before related.
	// Tiles Shopify does not recommend keep the collection order behind them.
	const recommendationCache = new Map();

	function fetchRecommendations(baseUrl, productId, intent) {
		const key = `${productId}:${intent}`;
		if (!recommendationCache.has(key)) {
			recommendationCache.set(
				key,
				fetch(`${baseUrl}.json?product_id=${productId}&limit=10&intent=${intent}`)
					.then((response) => (response.ok ? response.json() : { products: [] }))
					.then((data) => (data.products || []).map((product) => String(product.id)))
					.catch(() => [])
			);
		}
		return recommendationCache.get(key);
	}

	class CartUpsell extends HTMLElement {
		connectedCallback() {
			const list = this.querySelector(".cart-upsell__list");
			const baseUrl = this.dataset.recommendationsUrl;
			const productIds = (this.dataset.productIds || "").split(",").filter(Boolean);
			if (!list || !baseUrl || productIds.length === 0) return;

			const requests = [];
			["complementary", "related"].forEach((intent, group) => {
				productIds.forEach((productId) => {
					requests.push(
						fetchRecommendations(baseUrl, productId, intent).then((ids) => ({ group, ids }))
					);
				});
			});

			Promise.all(requests).then((results) => {
				if (!this.isConnected) return;
				const rank = new Map();
				results.forEach(({ group, ids }) => {
					ids.forEach((id, position) => {
						const value = group * 100 + position;
						if (!rank.has(id) || value < rank.get(id)) rank.set(id, value);
					});
				});
				if (rank.size === 0) return;
				const tiles = Array.from(list.children);
				const ordered = tiles
					.map((tile, index) => ({
						tile,
						index,
						value: rank.has(tile.dataset.productId) ? rank.get(tile.dataset.productId) : Infinity,
					}))
					.sort((a, b) => a.value - b.value || a.index - b.index);
				if (ordered.every((entry, index) => entry.tile === tiles[index])) return;
				ordered.forEach((entry) => list.appendChild(entry.tile));
			});
		}
	}

	if (!customElements.get("cart-upsell")) {
		customElements.define("cart-upsell", CartUpsell);
	}

	/* ------------------------------------------------------------------ */
	/* Free shipping bar in a currency other than the shop currency        */
	/* ------------------------------------------------------------------ */

	// The threshold is set in the shop currency and Liquid has no exchange
	// rate, so the bar is filled here from Shopify.currency.rate.
	if (!customElements.get("cart-free-shipping")) {
		class CartFreeShipping extends HTMLElement {
			connectedCallback() {
				const currency = window.Shopify && window.Shopify.currency;
				const rate = currency ? parseFloat(currency.rate) : NaN;
				if (!rate) return;

				const threshold = Math.round(Number(this.dataset.threshold) * rate);
				const total = Number(this.dataset.total);
				if (!threshold) return;

				const remaining = threshold - total;
				const text = this.querySelector(".cart-free-shipping__text");
				if (remaining <= 0) {
					text.textContent = this.dataset.achieved;
				} else {
					const amount = new Intl.NumberFormat(
						document.documentElement.lang || "de",
						{ style: "currency", currency: currency.active },
					).format(remaining / 100);
					text.innerHTML = this.dataset.remaining.replace("[amount]", amount);
				}
				const percent = Math.min(100, Math.round((total * 100) / threshold));
				this.querySelector(".cart-free-shipping__bar-fill").style.width = `${percent}%`;
				this.hidden = false;
			}
		}

		customElements.define("cart-free-shipping", CartFreeShipping);
	}
})();
